import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'Unknown error';
      const errorStack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';

      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          backgroundColor: '#000000',
          color: '#FF0000',
          fontFamily: 'monospace',
          padding: '40px',
          overflow: 'auto',
          zIndex: 9999,
        }}>
          <div style={{
            fontSize: '24px',
            fontWeight: 'bold',
            marginBottom: '20px',
            textTransform: 'uppercase',
            letterSpacing: '2px',
          }}>
            SYSTEM MALFUNCTION
          </div>
          
          <div style={{
            fontSize: '16px',
            marginBottom: '30px',
            color: '#FF4444',
            border: '1px solid #FF0000',
            padding: '20px',
            backgroundColor: '#1a0000',
          }}>
            <div style={{ marginBottom: '10px' }}>
              <strong>Error Message:</strong>
            </div>
            <div style={{ marginLeft: '20px', wordBreak: 'break-word' }}>
              {errorMessage}
            </div>
          </div>

          {errorStack && (
            <div style={{
              fontSize: '12px',
              marginBottom: '20px',
              color: '#FF6666',
              border: '1px solid #FF0000',
              padding: '15px',
              backgroundColor: '#1a0000',
              maxHeight: '300px',
              overflow: 'auto',
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Stack Trace:</strong>
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {errorStack}
              </pre>
            </div>
          )}

          {componentStack && (
            <div style={{
              fontSize: '12px',
              marginBottom: '20px',
              color: '#FF6666',
              border: '1px solid #FF0000',
              padding: '15px',
              backgroundColor: '#1a0000',
              maxHeight: '200px',
              overflow: 'auto',
            }}>
              <div style={{ marginBottom: '10px' }}>
                <strong>Component Stack:</strong>
              </div>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {componentStack}
              </pre>
            </div>
          )}

          <div style={{
            marginTop: '30px',
            fontSize: '14px',
            color: '#888888',
          }}>
            Check browser console for additional details.
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
